import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Label,
  Panel,
  PanelContent,
  PanelHeader,
  PanelTitle,
  Tag,
  TagsInput,
  Textarea,
} from "@diametral/design-system/react";

import { useReports, useSaveWeek, useWeekContext } from "../api/reports";
import { useSetGitIdentities } from "../api/session";
import type { ActivityReport, MissingReport, ReportField, ReportSectionInput, WeekContext, WeekProjectSection } from "../api/types";
import { ReportStatusTag } from "../components/Badges";
import { Page } from "../components/Page";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/States";
import { WeekSelect } from "../components/WeekSelect";
import { RequirePerson } from "../layout/Guards";
import { useCurrentUser } from "../lib/currentUser";
import { formatDeadline } from "../lib/format";
import { currentLanguage, t, Trans, useTranslation } from "../lib/i18n";
import { REPORT_FIELD_DEFAULTS } from "../lib/reference";
import { notifyInfo, notifySuccess } from "../lib/toast";
import { compareWeeks, currentWeek, isIsoWeek, shiftWeek, weekLabel, weekShortLabel } from "../lib/week";

type Draft = Omit<ReportSectionInput, "project">;
type Drafts = Record<string, Draft>;

function draftOf(section: WeekProjectSection): Draft {
  const r = section.report;
  return { done: r?.done ?? "", next: r?.next ?? "", blockers: r?.blockers ?? "", absent: r?.absent ?? false };
}

function sameDraft(a: Draft, b: Draft) {
  return a.done === b.done && a.next === b.next && a.blockers === b.blockers && a.absent === b.absent;
}

/** An optional section left blank is not sent: only what is owed, or already started, is saved. */
export function sectionsToSend(projects: WeekProjectSection[], drafts: Drafts): WeekProjectSection[] {
  return projects.filter((s) => {
    if (!s.optional || s.report) return true;
    const d = drafts[s.project] ?? draftOf(s);
    return d.absent || Boolean(d.done.trim() || d.next.trim() || d.blockers.trim());
  });
}

/** Client-side mirror of the backend's submit rule: « Réalisé » or « absent » per section. */
export function validateForSubmit(projects: WeekProjectSection[], drafts: Drafts): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const section of projects) {
    const draft = drafts[section.project];
    if (draft && !draft.absent && !draft.done.trim()) {
      errors[section.project] = t("Fill in “Done” or tick “Absent / no activity”.");
    }
  }
  return errors;
}

/** The backend sends its labels in French: outside French, the translated defaults stand in. */
function fieldCopy(fields: ReportField[], name: ReportField["name"]) {
  const fallback = { name, label: REPORT_FIELD_DEFAULTS[name].label, help: REPORT_FIELD_DEFAULTS[name].help };
  if (currentLanguage() !== "fr") return fallback;
  return fields.find((f) => f.name === name) ?? fallback;
}

export function CraPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const requested = params.get("week");
  const week = isIsoWeek(requested) ? requested : "default";

  function goTo(next: string) {
    setParams((current) => {
      const copy = new URLSearchParams(current);
      copy.set("week", next);
      return copy;
    });
  }

  return (
    <Page
      kicker={t("My space")}
      title={t("My activity report")}
      description={t(
        "One report per week, one section per project. Drafts are visible only to you; a submitted report can be read by everyone.",
      )}
    >
      <RequirePerson>
        {requested && !isIsoWeek(requested) ? (
          <Callout type="warning">{t("Unreadable week “{{week}}”: showing the default week.", { week: requested })}</Callout>
        ) : null}
        <div className="ada-split">
          <WeekForm week={week} onWeekChange={goTo} />
          <aside className="ada-stack">
            <MissingWeeks />
            <PastWeeks />
            <GitIdentities />
          </aside>
        </div>
      </RequirePerson>
    </Page>
  );
}

function WeekForm({ week, onWeekChange }: { week: string; onWeekChange: (week: string) => void }) {
  const { t } = useTranslation();
  const context = useWeekContext(week);
  if (context.isPending) return <LoadingBlock lines={8} label={t("Loading the week…")} />;
  if (context.isError) return <ErrorBlock error={context.error} onRetry={() => void context.refetch()} />;
  // Keyed by week: switching weeks starts from that week's saved state.
  return <WeekEditor key={context.data.week} context={context.data} onWeekChange={onWeekChange} />;
}

function WeekEditor({ context, onWeekChange }: { context: WeekContext; onWeekChange: (week: string) => void }) {
  const { t } = useTranslation();
  const save = useSaveWeek();
  const initial = useMemo(
    () => Object.fromEntries(context.projects.map((s) => [s.project, draftOf(s)])) as Drafts,
    [context.projects],
  );
  const [drafts, setDrafts] = useState<Drafts>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const previous = useRef(initial);
  const adoptSaved = useRef(false);

  // When the server copy changes (our own save, or an edit made elsewhere, e.g. through Claude),
  // adopt it — except for sections edited here and not saved yet, unless we just saved them.
  useEffect(() => {
    const before = previous.current;
    previous.current = initial;
    const force = adoptSaved.current;
    adoptSaved.current = false;
    setDrafts((current) =>
      Object.fromEntries(
        Object.entries(initial).map(([project, server]) => {
          const local = current[project];
          const old = before[project];
          const keepLocal = !force && local && old && !sameDraft(local, old);
          return [project, keepLocal ? local : server];
        }),
      ),
    );
  }, [initial]);

  const thisWeek = currentWeek();
  const future = compareWeeks(context.week, thisWeek) > 0;
  const beforeStart = Boolean(context.cra_start_week && compareWeeks(context.week, context.cra_start_week) < 0);
  const dirty = context.projects.some((s) => !sameDraft(drafts[s.project] ?? draftOf(s), draftOf(s)));
  const counted = context.projects.filter((s) => !s.optional || s.report);
  const allSubmitted = counted.length > 0 && counted.every((s) => s.report?.status === "submitted");
  const fields = context.fields;

  function changeWeek(next: string) {
    if (!next || next === context.week) return;
    if (dirty && !window.confirm(t("Some changes are not saved. Change week anyway?"))) return;
    onWeekChange(next);
  }

  function update(project: string, patch: Partial<Draft>) {
    setDrafts((current) => ({ ...current, [project]: { ...(current[project] as Draft), ...patch } }));
    if (errors[project]) setErrors((current) => ({ ...current, [project]: "" }));
  }

  function persist(submit: boolean) {
    const chosen = sectionsToSend(context.projects, drafts);
    if (!chosen.length) {
      notifyInfo(t("Nothing to save"), t("Fill in at least one section."));
      return;
    }
    if (submit) {
      const found = validateForSubmit(chosen, drafts);
      setErrors(found);
      if (Object.keys(found).length) return;
    }
    const sections = chosen.map((s) => ({ project: s.project, ...(drafts[s.project] ?? draftOf(s)) }));
    save.mutate(
      { week: context.week, submit, sections },
      {
        onSuccess: (result) => {
          // The backend trims the texts: match it, then take its copy when it comes back.
          adoptSaved.current = true;
          setDrafts((current) =>
            Object.fromEntries(
              Object.entries(current).map(([project, d]) => [
                project,
                { ...d, done: d.done.trim(), next: d.next.trim(), blockers: d.blockers.trim() },
              ]),
            ),
          );
          if (submit || allSubmitted) {
            notifySuccess(
              allSubmitted && !submit ? t("Report updated") : t("Report submitted"),
              result.missing.length
                ? t("{{count}} sections still to file for this week.", { count: result.missing.length })
                : t("{{week}}: complete.", { week: weekLabel(result.week) }),
            );
          } else {
            notifySuccess(t("Draft saved"), t("Only you can see it until it is submitted."));
          }
        },
      },
    );
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    persist(true);
  }

  const canSave = !future && context.projects.length > 0;

  return (
    <form className="ada-stack ada-stack--loose" onSubmit={onSubmit} noValidate aria-label={t("Report {{week}}", { week: weekLabel(context.week) })}>
      <div className="ada-stack ada-stack--tight">
        <div className="ada-row">
          <div className="ada-cluster">
            <Button type="button" variant="outline" size="sm" onClick={() => changeWeek(shiftWeek(context.week, -1))}>
              {t("‹ Previous week")}
            </Button>
            <WeekSelect label={t("Report week")} value={context.week} onChange={changeWeek} count={26} last={thisWeek} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={compareWeeks(context.week, thisWeek) >= 0}
              onClick={() => changeWeek(shiftWeek(context.week, 1))}
            >
              {t("Next week ›")}
            </Button>
          </div>
          <div className="ada-cluster">
            {allSubmitted ? <Tag tone="success">{t("Submitted")}</Tag> : null}
            {context.overdue ? <Tag tone="danger">{t("Overdue")}</Tag> : null}
            {dirty ? <Tag tone="warning">{t("Unsaved changes")}</Tag> : null}
          </div>
        </div>
        <h2 className="ada-section-title">{weekLabel(context.week)}</h2>
        <p className="ada-muted">
          <Trans
            i18nKey="Due before <strong>{{deadline}}</strong>."
            values={{ deadline: formatDeadline(context.deadline) }}
            components={{ strong: <strong /> }}
          />
        </p>
      </div>

      {future ? (
        <Callout type="info">{t("This week has not started yet: a report cannot be filed for a future week.")}</Callout>
      ) : null}
      {beforeStart ? (
        <Callout type="info">
          {t("Reports are only required from week {{week}} onwards: this one is optional.", {
            week: context.cra_start_week ? weekShortLabel(context.cra_start_week) : "",
          })}
        </Callout>
      ) : null}
      {context.guidelines ? (
        <Callout type="info" heading={t("Guidelines")}>
          {context.guidelines}
        </Callout>
      ) : null}

      {context.projects.length === 0 ? (
        <EmptyBlock
          icon="calendar"
          title={t("No report expected this week")}
          description={t("You were not assigned to any project that week.")}
        />
      ) : (
        context.projects.map((section) => {
          const draft = drafts[section.project] ?? draftOf(section);
          const id = (name: string) => `cra-${section.project}-${name}`;
          const error = errors[section.project];
          return (
            <Panel key={section.project}>
              <PanelHeader ruled>
                <div className="ada-row">
                  <PanelTitle>
                    <Link className="ada-link" to={`/projects/${section.project}`}>
                      {section.project_title}
                    </Link>
                  </PanelTitle>
                  {section.report ? (
                    <ReportStatusTag status={section.report.status} />
                  ) : section.optional ? (
                    <Tag tone="neutral">{t("Optional")}</Tag>
                  ) : (
                    <Tag tone="danger">{t("To file")}</Tag>
                  )}
                </div>
                {section.streams.length ? (
                  <p className="ada-faint">
                    {t("{{count}} streams: {{list}}", { count: section.streams.length, list: section.streams.join(", ") })}
                  </p>
                ) : null}
              </PanelHeader>
              <PanelContent>
                <div className="ada-form">
                  <Field>
                    <FieldLabel htmlFor={id("done")}>{fieldCopy(fields, "done").label}</FieldLabel>
                    <Textarea
                      id={id("done")}
                      rows={5}
                      value={draft.done}
                      aria-invalid={error ? true : undefined}
                      onChange={(e) => update(section.project, { done: e.target.value })}
                    />
                    <FieldDescription>{fieldCopy(fields, "done").help}</FieldDescription>
                    <FieldError>{error || null}</FieldError>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={id("next")}>{fieldCopy(fields, "next").label}</FieldLabel>
                    <Textarea id={id("next")} rows={3} value={draft.next} onChange={(e) => update(section.project, { next: e.target.value })} />
                    <FieldDescription>{fieldCopy(fields, "next").help}</FieldDescription>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={id("blockers")}>{fieldCopy(fields, "blockers").label}</FieldLabel>
                    <Textarea
                      id={id("blockers")}
                      rows={3}
                      value={draft.blockers}
                      onChange={(e) => update(section.project, { blockers: e.target.value })}
                    />
                    <FieldDescription>{fieldCopy(fields, "blockers").help}</FieldDescription>
                  </Field>
                  <div className="ada-check">
                    <Checkbox
                      id={id("absent")}
                      checked={draft.absent}
                      onCheckedChange={(checked: boolean) => update(section.project, { absent: checked })}
                    />
                    <Label htmlFor={id("absent")}>{fieldCopy(fields, "absent").label}</Label>
                  </div>
                  {section.repositories.length ? (
                    <details>
                      <summary className="ada-faint">{t("{{count}} linked repositories", { count: section.repositories.length })}</summary>
                      <ul className="ada-list">
                        {section.repositories.map((repo) => (
                          <li key={repo.url} className="ada-mono">
                            {repo.url}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              </PanelContent>
            </Panel>
          );
        })
      )}

      {canSave ? (
        <div className="ada-cluster ada-cluster--end">
          {allSubmitted ? (
            <span className="ada-faint">{t("This report is submitted: your changes will be visible to everyone.")}</span>
          ) : (
            <Button type="button" variant="outline" loading={save.isPending && !save.variables?.submit} onClick={() => persist(false)}>
              {t("Save draft")}
            </Button>
          )}
          <Button type="submit" variant="primary" loading={save.isPending && Boolean(save.variables?.submit)}>
            {allSubmitted ? t("Save changes") : t("Submit")}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

/** Weeks past their deadline with a missing section (from GET /api/me). */
function MissingWeeks() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const byWeek = useMemo(() => {
    const groups = new Map<string, MissingReport[]>();
    for (const item of me.missing_reports) groups.set(item.week, [...(groups.get(item.week) ?? []), item]);
    return [...groups.entries()].sort(([a], [b]) => compareWeeks(b, a));
  }, [me.missing_reports]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("To file")}</CardTitle>
      </CardHeader>
      <CardContent>
        {byWeek.length ? (
          <ul className="ada-list">
            {byWeek.map(([week, items]) => (
              <li key={week} className="ada-stack ada-stack--tight">
                <Link className="ada-link" to={`/reports?week=${week}`}>
                  {isIsoWeek(week) ? weekShortLabel(week) : week}
                </Link>
                <span className="ada-faint">{items.map((i) => i.project_title).join(", ")}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ada-faint">{t("No overdue reports.")}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** My own reports (drafts included), by week. */
function PastWeeks() {
  const { t } = useTranslation();
  const mine = useReports({ mine: true, limit: 60 });
  const weeks = useMemo(() => {
    const groups = new Map<string, ActivityReport[]>();
    for (const r of mine.data ?? []) groups.set(r.week, [...(groups.get(r.week) ?? []), r]);
    return [...groups.entries()].sort(([a], [b]) => compareWeeks(b, a)).slice(0, 12);
  }, [mine.data]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("My recent weeks")}</CardTitle>
      </CardHeader>
      <CardContent>
        {mine.isPending ? (
          <LoadingBlock lines={3} />
        ) : mine.isError ? (
          <ErrorBlock error={mine.error} onRetry={() => void mine.refetch()} />
        ) : weeks.length ? (
          <ul className="ada-list">
            {weeks.map(([week, reports]) => (
              <li key={week} className="ada-stack ada-stack--tight">
                <Link className="ada-link" to={`/reports?week=${week}`}>
                  {isIsoWeek(week) ? weekShortLabel(week) : week}
                </Link>
                <span className="ada-cluster">
                  {reports.map((r) => (
                    <span key={r.id} className="ada-cluster">
                      <span className="ada-faint">{r.project_title}</span>
                      <ReportStatusTag status={r.status} />
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ada-faint">{t("No reports yet.")}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** Commit authors used to draft the CRA from Git (with Claude). The Boond email always counts. */
function GitIdentities() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const saved = me.person?.git_identities ?? [];
  const [value, setValue] = useState<string[]>(saved);
  const save = useSetGitIdentities();
  const savedKey = saved.join("\n");

  useEffect(() => setValue(savedKey ? savedKey.split("\n") : []), [savedKey]);

  const dirty = value.join("\n") !== savedKey;
  return (
    <Panel>
      <PanelHeader ruled>
        <PanelTitle>{t("My Git identities")}</PanelTitle>
      </PanelHeader>
      <PanelContent>
        <div className="ada-stack ada-stack--tight">
          <TagsInput
            aria-label={t("Git identities (emails or author names)")}
            value={value}
            onValueChange={setValue}
            max={20}
            placeholder={t("first.last@example.com, Enter to add")}
          />
          <p className="ada-faint">
            {t("The authors of your commits, to draft the report from Git with Claude.")}
            {me.person?.email ? ` ${t("Your Boond email ({{email}}) is always included.", { email: me.person.email })}` : ""}
          </p>
          <div>
            <Button
              size="sm"
              variant="outline"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate(value, { onSuccess: () => notifySuccess(t("Git identities saved")) })}
            >
              {t("Save")}
            </Button>
          </div>
        </div>
      </PanelContent>
    </Panel>
  );
}
